import {type FC, type MouseEvent, useCallback, useEffect, useMemo, useState} from 'react';
import {styled} from 'styled-components';

import type {FrontendTf1Video} from '@repo/shared/api/tf1';
import {groupTf1Seasons} from '@repo/shared/lib/tf1_catalogue';

import {UnthemedButton} from '@repo/shared-web/components/core/button';
import {LoadingIndicator} from '@repo/shared-web/components/core/loading_indicator';
import {SvgIcon} from '@repo/shared-web/components/core/svg_icon';
import {downloadIcon} from '@repo/shared-web/components/icons/download_icon';
import {notifyError} from '@repo/shared-web/lib/notification';

import {apiCall} from '@repo/website-frontend/lib/api';
import {formatBytes} from '@repo/website-frontend/lib/bytes';
import {
  fetchKohLantaCatalogue,
  useKohLantaCatalogue,
} from '@repo/website-frontend/stores/koh_lanta_store';

interface KohLantaPageProps {}
const TF1_LOGO_URL = new URL('../assets/tf1-logo.png', import.meta.url).href;
const PAUSE_REASONS: Record<string, string> = {
  access_required: 'TF1 denied access; account or playback access needs investigation',
  region_blocked: 'TF1 reported a location restriction',
  invalid_response:
    'TF1 returned an unexpected response; the provider integration needs investigation',
  media_tool_unavailable: 'a required video processing tool is unavailable',
};
const duration = (seconds: number | undefined): string =>
  seconds ? `${Math.floor(seconds / 60)}m ${Math.floor(seconds % 60)}s` : '—';

export const KohLantaPage: FC<KohLantaPageProps> = () => {
  const {data, loading, error, loadedAt} = useKohLantaCatalogue();
  const [selectedId, setSelectedId] = useState<string>();
  useEffect(() => {
    void fetchKohLantaCatalogue().catch(notifyError);
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void fetchKohLantaCatalogue().catch(notifyError);
    }, 30_000);
    return () => clearInterval(timer);
  }, []);
  const groups = useMemo(
    () =>
      groupTf1Seasons(data?.seasons ?? [], data?.videos ?? []).filter(group =>
        group.videos.some(
          video =>
            video.kind === 'episode' && ['complete', 'queued', 'downloading'].includes(video.state),
        ),
      ),
    [data],
  );
  const selected = groups.find(group => group.season.id === selectedId) ?? groups[0];
  const videos = selected?.videos ?? [];
  const seasonCount = groups.filter(group => group.season.season !== undefined).length;
  const totalParts = groups.reduce((total, group) => total + group.episodeParts, 0);
  const downloaded = groups.reduce((total, group) => total + group.downloaded, 0);
  const queued = groups.reduce(
    (total, group) =>
      total +
      group.videos.filter(video => video.kind === 'episode' && video.state === 'queued').length,
    0,
  );
  const handleSeason = useCallback((event: MouseEvent<HTMLElement>) => {
    const id = event.currentTarget.getAttribute('data-season-id');
    if (id) setSelectedId(id);
  }, []);
  const handleDownload = useCallback(async (event: MouseEvent<HTMLElement>) => {
    const videoId = event.currentTarget.getAttribute('data-video-id');
    if (!videoId) return;
    const {url} = await apiCall('POST /get-koh-lanta-download-link', {videoId});
    const link = document.createElement('a');
    link.href = url;
    link.download = '';
    link.style.display = 'none';
    document.body.append(link);
    link.click();
    link.remove();
  }, []);

  return (
    <Wrapper>
      {error ? <Notice role="alert">{error}</Notice> : null}
      {!data && loading ? (
        <Loading role="status">
          <LoadingIndicator size={20} /> Loading the catalogue…
        </Loading>
      ) : null}
      {data && !data.enabled ? (
        <Empty>The Koh-Lanta catalogue is not configured in this environment.</Empty>
      ) : null}
      {data?.enabled ? (
        <>
          {data.downloadsPaused ? (
            <Notice>
              Downloads are paused:{' '}
              {PAUSE_REASONS[data.pauseReason ?? ''] ?? 'paused for investigation'}. The catalogue
              remains available.
            </Notice>
          ) : null}
          {!data.downloadsPaused && (data.cooldownUntil ?? 0) > Date.now() ? (
            <Notice>
              Downloads are backing off until {new Date(data.cooldownUntil!).toLocaleString()}.
            </Notice>
          ) : null}
          <Layout>
            <Seasons aria-label="Koh-Lanta seasons">
              {groups.map(group => {
                const name = group.season.label
                  .replace(/^(?:Saison|Season)\s+\d+\s*(?:[-–—:]\s*)?/iu, '')
                  .trim();
                const episodeCount = new Set(
                  group.videos
                    .filter(video => video.kind === 'episode' && video.episode !== undefined)
                    .map(video => video.episode),
                ).size;
                return (
                  <SeasonButton
                    key={group.season.id}
                    data-season-id={group.season.id}
                    onClick={handleSeason}
                    $active={selected?.season.id === group.season.id}
                    aria-pressed={selected?.season.id === group.season.id}
                  >
                    <strong>
                      {group.season.season === undefined
                        ? group.season.label
                        : `Saison ${group.season.season} · ${episodeCount} ${episodeCount === 1 ? 'épisode' : 'épisodes'}`}
                    </strong>
                    <span>
                      {group.season.season === undefined ? `${group.videos.length} extras` : name}
                    </span>
                  </SeasonButton>
                );
              })}
            </Seasons>
            <Content>
              <ContentHeader>
                <h1>{selected?.season.label ?? 'Episodes'}</h1>
              </ContentHeader>
              {videos.length ? (
                <EpisodeList aria-label="Épisodes">
                  {videos.map(video => (
                    <EpisodeRow key={video.videoId} video={video} onDownload={handleDownload} />
                  ))}
                </EpisodeList>
              ) : (
                <Empty>
                  {groups.length
                    ? 'No videos in this season.'
                    : data.videos.length
                      ? 'No seasons have downloaded or pending episode parts.'
                      : 'The first catalogue scan has not recorded any videos yet.'}
                </Empty>
              )}
            </Content>
          </Layout>
          <Footer>
            {downloaded} / {totalParts} episode parts downloaded · {seasonCount}{' '}
            {seasonCount === 1 ? 'season' : 'seasons'} available · {queued}{' '}
            {queued === 1 ? 'part' : 'parts'} queued
            {loadedAt ? ` · Updated ${new Date(loadedAt).toLocaleTimeString()}.` : ''}
          </Footer>
        </>
      ) : null}
    </Wrapper>
  );
};
KohLantaPage.displayName = 'KohLantaPage';

interface EpisodeRowProps {
  video: FrontendTf1Video;
  onDownload: (event: MouseEvent<HTMLElement>) => Promise<void>;
}
const EpisodeRow: FC<EpisodeRowProps> = ({video, onDownload}) => {
  const label =
    video.kind === 'episode' && video.episode !== undefined
      ? `Épisode ${video.episode}${video.part ? ` · Partie ${video.part}` : ''}`
      : video.title;
  return (
    <EpisodeItem>
      <EpisodeDetails>
        <EpisodeTitle>{label}</EpisodeTitle>
        <Metadata>
          {duration(video.durationSeconds)}
          {video.bytes ? ` · ${formatBytes(video.bytes, 0)}` : ''}
        </Metadata>
      </EpisodeDetails>
      <EpisodeActions>
        <Source
          href={video.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Regarder ${label} sur TF1 (nouvel onglet)`}
          title="Voir sur TF1"
        >
          <Tf1Logo src={TF1_LOGO_URL} alt="TF1" height={20} />
        </Source>
        {video.state === 'complete' ? (
          <Download
            data-video-id={video.videoId}
            onClickAsync={onDownload}
            aria-label={`Télécharger ${label}`}
            title="Télécharger le fichier MP4"
          >
            <SvgIcon icon={downloadIcon} size={18} color="currentColor" aria-hidden="true" />
          </Download>
        ) : null}
      </EpisodeActions>
    </EpisodeItem>
  );
};
EpisodeRow.displayName = 'EpisodeRow';

const Wrapper = styled.main`
  width: 100%;
  max-width: 1260px;
  box-sizing: border-box;
  padding: 32px 28px;
  margin: 0 auto;
  @media (max-width: 600px) {
    padding: 20px 14px;
  }
`;
const Layout = styled.div`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 20px;
  align-items: start;
  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Seasons = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 6px;
  @media (max-width: 760px) {
    flex-direction: row;
    overflow-x: auto;
    padding-bottom: 6px;
  }
`;
const SeasonButton = styled(UnthemedButton)<{$active: boolean}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 13px 16px;
  border-radius: 8px;
  flex-shrink: 0;
  max-width: 100%;
  box-sizing: border-box;
  text-align: left;
  background: ${p => (p.$active ? '#293446' : 'transparent')};
  color: ${p => (p.$active ? '#fff' : '#bac2d0')};
  border: 1px solid ${p => (p.$active ? '#8cbfff55' : 'transparent')};
  strong {
    font-size: 14px;
    font-weight: 500;
  }
  span {
    font-size: 11px;
    color: #a5aebd;
    line-height: 1.5;
  }
  &:hover {
    background: #293446;
  }
  &:focus-visible {
    outline: 2px solid #8cbfff;
    outline-offset: 2px;
  }
`;
const Content = styled.section`
  min-width: 0;
  border: 1px solid #ffffff12;
  border-radius: 10px;
  background: #1b202a;
  overflow: hidden;
`;
const ContentHeader = styled.div`
  padding: 20px;
  h1 {
    font-size: 20px;
    margin: 0;
  }
`;
const Metadata = styled.div`
  font-size: 12px;
  color: #939eaf;
  margin-top: 4px;
  line-height: 1.5;
`;
const Download = styled(UnthemedButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid #ffffff26;
  border-radius: 6px;
  background: #ffffff05;
  color: #c3cbd7;
  &:hover {
    background: #ffffff12;
    color: #fff;
  }
  &:focus-visible {
    outline: 2px solid #8cbfff;
    outline-offset: 2px;
  }
`;
const EpisodeList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;
const EpisodeItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 16px 20px;
  border-top: 1px solid #ffffff10;
  @media (max-width: 600px) {
    gap: 12px;
    padding: 14px;
  }
`;
const EpisodeDetails = styled.div`
  flex: 1;
  min-width: 0;
`;
const EpisodeActions = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  min-width: 104px;
  gap: 12px;
`;
const EpisodeTitle = styled.strong`
  display: block;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  overflow-wrap: anywhere;
  @media (max-width: 600px) {
    font-size: 13px;
  }
`;
const Source = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  min-height: 36px;
  border-radius: 4px;
  &:hover {
    opacity: 0.8;
  }
  &:focus-visible {
    outline: 2px solid #8cbfff;
    outline-offset: 2px;
  }
`;
const Tf1Logo = styled.img`
  display: block;
  width: auto;
  height: 20px;
`;
const Footer = styled.p`
  font-size: 11px;
  color: #8e99aa;
  margin: 18px 0;
`;
const Notice = styled.div`
  padding: 14px 18px;
  background: #ffcc7b12;
  color: #ffda9f;
  border-radius: 8px;
  font-size: 13px;
  margin: 0 0 18px;
`;
const Empty = styled.p`
  color: #a3adbc;
  padding: 20px;
  margin: 0;
  font-size: 14px;
`;
const Loading = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 24px 0;
  color: #a3adbc;
  font-size: 14px;
`;
